import { Box, Stack } from '@mui/material';

import { PresentationPlanning01Icon } from '@/components/icons/PresentationPlanning01Icon';
import { PresentationMarkdownField } from '@/modules/presentation/components/PresentationMarkdownField/PresentationMarkdownField';
import { COLORS } from '@/styles/tokens/colors';

/** One opportunity card. Mirrors the planned shape of
 *  `PresentationPlanningOpportunitiesItem` once the backend lands the
 *  6-field schema (PR2): an `*Label` + `*Body` pair for each of the
 *  three sections that render together on a single card (Opportunity
 *  above the divider; Rationale + Next step in a 2-column grid below).
 *  Until then this is the slide-local shape; the consumer maps to/from
 *  the backend type. */
export interface ObservationAndOpportunity {
  id: string;
  /** Eyebrow above the opportunity body. Falls back to the
   *  `OPPORTUNITY` placeholder via `PresentationMarkdownField` when
   *  empty. */
  opportunityLabel: string;
  opportunityBody: string;
  /** Eyebrow above the rationale body. Falls back to the `RATIONALE`
   *  placeholder when empty. */
  rationaleLabel: string;
  rationaleBody: string;
  /** Eyebrow above the next-step body. Falls back to the `NEXT STEP`
   *  placeholder when empty. */
  nextStepLabel: string;
  nextStepBody: string;
}

/** Field names a card edit can target. Used by the slide's
 *  `onFieldChange` callback so a single callback funnels every text
 *  edit (label OR body, on any of the three sections) through one
 *  routing path. */
export type ObservationAndOpportunityField =
  | 'opportunityLabel'
  | 'opportunityBody'
  | 'rationaleLabel'
  | 'rationaleBody'
  | 'nextStepLabel'
  | 'nextStepBody';

/** Canonical default eyebrow text for a card's three sections.
 *  Applied at load time by the form mapping (`mapBundleToForm`) so the
 *  form state — and any subsequent save — carries the default instead
 *  of the backend's empty/null. Once a bundle is saved, the backend
 *  stops returning blanks for these labels. Also passed to the
 *  underlying `PresentationMarkdownField` as the placeholder, which
 *  covers the in-session "user cleared the label" window between
 *  loads. Stored in title case; the `eyebrow` variant applies
 *  `text-transform: uppercase`, so the visible rendering is
 *  "OPPORTUNITY" etc. */
export const DEFAULT_OPPORTUNITY_LABEL = 'Opportunity';
export const DEFAULT_RATIONALE_LABEL = 'Rationale';
export const DEFAULT_NEXT_STEP_LABEL = 'Next step';

export interface ObservationAndOpportunityCardProps {
  opportunity: ObservationAndOpportunity;
  /** Single sink for every text edit on the card. Omit to render the
   *  card as static markdown (print / read-only mode). */
  onFieldChange?: (field: ObservationAndOpportunityField, next: string) => void;
}

/**
 * Single planning-opportunity card. Renders an icon-in-circle on the
 * left and the opportunity content on the right: opportunity
 * label/body above a hairline divider, then rationale (col 1) and
 * next step (col 2) below. Figma: 847:14998 (canonical card in frame
 * 847:14853).
 *
 * The card itself doesn't manage reorder UX — the slide wraps it with
 * `cardStack.Wrapper` so the parent toolbar (move / duplicate /
 * delete) is applied uniformly to every card on the slide.
 */
export function ObservationAndOpportunityCard({
  opportunity,
  onFieldChange,
}: ObservationAndOpportunityCardProps) {
  // Pre-resolve per-field change callbacks once so the JSX below stays
  // declarative — `PresentationMarkdownField` re-renders on every text
  // change, so allocating these inline would churn its memoized
  // toolbar component.
  const onChangeFor = (field: ObservationAndOpportunityField) =>
    onFieldChange ? (next: string) => onFieldChange(field, next) : undefined;

  return (
    <Stack
      direction="row"
      gap={2}
      sx={{
        backgroundColor: COLORS.NEUTRAL_GRAY[50],
        // theme.shape.borderRadius is 4px; `2` ≈ 8px to match Figma's
        // 8px corner radius.
        borderRadius: 2,
        p: 2,
        alignItems: 'flex-start',
      }}
    >
      <Box
        sx={(theme) => ({
          flexShrink: 0,
          width: 36,
          height: 36,
          borderRadius: '50%',
          backgroundColor: theme.palette.dataVisualizationPrimary.main,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        })}
      >
        <PresentationPlanning01Icon size={24} color={COLORS.PRIMITIVES.WHITE} />
      </Box>
      <Stack gap={1.5} sx={{ flex: 1, minWidth: 0 }}>
        <ObservationAndOpportunityFieldRow
          label={opportunity.opportunityLabel}
          body={opportunity.opportunityBody}
          labelPlaceholder={DEFAULT_OPPORTUNITY_LABEL}
          onLabelChange={onChangeFor('opportunityLabel')}
          onBodyChange={onChangeFor('opportunityBody')}
        />
        <Divider />
        {/* The 48px column gap matches Figma node 13850:228
         *  (`gap-[48px]`). Two equal flex columns so Rationale (col 1)
         *  and Next step (col 2) split the row evenly. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            columnGap: 6,
            rowGap: 1.5,
          }}
        >
          <ObservationAndOpportunityFieldRow
            label={opportunity.rationaleLabel}
            body={opportunity.rationaleBody}
            labelPlaceholder={DEFAULT_RATIONALE_LABEL}
            onLabelChange={onChangeFor('rationaleLabel')}
            onBodyChange={onChangeFor('rationaleBody')}
          />
          <ObservationAndOpportunityFieldRow
            label={opportunity.nextStepLabel}
            body={opportunity.nextStepBody}
            labelPlaceholder={DEFAULT_NEXT_STEP_LABEL}
            onLabelChange={onChangeFor('nextStepLabel')}
            onBodyChange={onChangeFor('nextStepBody')}
          />
        </Box>
      </Stack>
    </Stack>
  );
}

function Divider() {
  return (
    <Box
      sx={{ height: '1px', width: '100%', backgroundColor: COLORS.GRAY[300] }}
    />
  );
}

interface ObservationAndOpportunityFieldRowProps {
  label: string;
  body: string;
  /** Placeholder eyebrow rendered when `label` is empty. Defaulting
   *  is normally handled at load time by `mapBundleToForm` so this
   *  placeholder only surfaces in the transient "user cleared the
   *  label" window before the next save+reload. We deliberately do
   *  NOT pre-resolve the default into the field's `content` here:
   *  `PresentationMarkdownField` caches the last-committed value in
   *  internal state and only releases it when the `content` prop
   *  changes — pre-resolving would freeze `content` at the default
   *  even after the user clears the field, leaving the field stuck
   *  on a generic "Click to add text" placeholder. */
  labelPlaceholder: string;
  onLabelChange?: (next: string) => void;
  onBodyChange?: (next: string) => void;
}

function ObservationAndOpportunityFieldRow({
  label,
  body,
  labelPlaceholder,
  onLabelChange,
  onBodyChange,
}: ObservationAndOpportunityFieldRowProps) {
  return (
    <Stack gap={0.5}>
      <PresentationMarkdownField
        content={label}
        onBlur={onLabelChange}
        placeholder={labelPlaceholder}
        variant="eyebrow"
      />
      <PresentationMarkdownField
        content={body}
        onBlur={onBodyChange}
        placeholder="Add details"
      />
    </Stack>
  );
}
